import type { CellClickedEvent } from 'ag-grid-community';
import { useCallback } from 'react';

import useDeviceQueryParam from '@/pages/Administration/Devices/Devices.hooks';

import type { DeviceTreeNode } from '../DevicesTable.types';

/**
 * Custom hook for managing device row selection
 *
 * Handles cell click events to navigate to device or source detail pages.
 * Filters out clicks on action columns and auto-group columns to prevent
 * unwanted navigation.
 *
 * @returns Object containing onCellClicked callback
 *
 * @example
 * ```tsx
 * const selection = useDevicesRowSelection();
 * <AgGridReact
 *   onCellClicked={selection.onCellClicked}
 * />
 * ```
 */
export const useDevicesRowSelection = () => {
  const { addDeviceFilter, addSourceFilter } = useDeviceQueryParam();

  /**
   * Handle cell click - navigate to device or source detail
   * Ignores clicks on action columns and auto-group columns
   */
  const onCellClicked = useCallback(
    (event: CellClickedEvent<DeviceTreeNode>) => {
      const node = event.data;
      if (!node) return;

      // Ignore clicks on the actions column and auto-group column
      if (!event.column.getColDef().field) {
        return;
      }

      // Navigate to device or source detail
      if (node.type === 'device' && node.deviceData) {
        addDeviceFilter(node.deviceData.device_uid);
      } else if (node.type === 'source' && node.sourceData) {
        addSourceFilter(
          node.deviceData?.device_uid || '',
          node.sourceData.source_uid
        );
      }
    },
    [addDeviceFilter, addSourceFilter]
  );

  return {
    onCellClicked
  };
};
